import React, { useMemo, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Factory,
  Gauge,
  Layers,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  SlidersHorizontal,
  Truck,
} from "lucide-react";

import { mock, buildWhatsAppUrl } from "@/mock";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import WhatsAppFab from "@/components/WhatsAppFab";

const NavLink = ({ href, children, onClick }) => (
  <a
    href={href}
    onClick={onClick}
    className="whitespace-nowrap text-[16px] font-semibold text-primary/90 transition-colors hover:text-primary"
  >
    {children}
  </a>
);

function sectionIdToLabel(id) {
  const map = {
    quienes: "Quiénes somos",
    servicios: "Servicios",
    productos: "Productos",
    contacto: "Contacto",
  };
  return map[id] || id;
}

function buildQuoteMessage({ nombre, empresa, email, telefono, mensaje }) {
  const lines = [
    "Hola, quisiera solicitar una cotización.",
    nombre ? `Nombre: ${nombre}` : null,
    empresa ? `Empresa: ${empresa}` : null,
    email ? `Email: ${email}` : null,
    telefono ? `Teléfono: ${telefono}` : null,
    mensaje ? `Detalle: ${mensaje}` : null,
  ].filter(Boolean);

  return lines.join("\n");
}

function formatArMobilePhone(input) {
  // Simple mask: digits only + spaces for readability.
  // No fixed length restriction (supports interior phone lengths).
  const digits = String(input || "").replace(/\D/g, "");
  if (!digits) return "";

  // Heuristic:
  // - If starts with 11, keep 2-digit prefix (AMBA mobile).
  // - Otherwise, keep 3-digit prefix for longer numbers (common area codes).
  const prefixLen = digits.startsWith("11") ? 2 : digits.length >= 11 ? 3 : 2;

  const prefix = digits.slice(0, prefixLen);
  const rest = digits.slice(prefixLen);

  const chunks = [];
  for (let i = 0; i < rest.length; i += 4) chunks.push(rest.slice(i, i + 4));

  return [prefix, ...chunks].filter(Boolean).join(" ").trim();
}


export default function Landing() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [form, setForm] = useState({
    nombre: "",
    empresa: "",
    email: "",
    telefono: "",
    mensaje: "",
  });

  const waDefaultUrl = useMemo(() => {
    return buildWhatsAppUrl({
      phoneDigits: mock.whatsapp.phoneDigits,
      message: mock.whatsapp.defaultMessage,
    });
  }, []);

  const quoteUrlFromForm = useMemo(() => {
    const message = buildQuoteMessage(form);

    return buildWhatsAppUrl({
      phoneDigits: mock.whatsapp.phoneDigits,
      message,
    });
  }, [form]);

  // servicesTab removed (simplified services section)

  const nav = ["quienes", "servicios", "productos", "contacto"].map((id) => ({
    id,
    label: sectionIdToLabel(id),
  }));

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const onSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...form,
      source: "landing",
      page_url: window.location.href,
      user_agent: navigator.userAgent,
    };

    try {
      await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      toast.success("Consulta enviada. Abrimos WhatsApp para continuar.", {
        description:
          "Si preferís, también podés escribirnos por email y retomamos por ese canal.",
      });

      window.open(quoteUrlFromForm, "_blank", "noreferrer");

      setForm({ nombre: "", empresa: "", email: "", telefono: "", mensaje: "" });
    } catch {
      toast.error(
        "No pudimos enviar la consulta. Probá nuevamente o escribinos por WhatsApp.",
      );
      window.open(quoteUrlFromForm, "_blank", "noreferrer");
    }
  };

  return (
    <div className="at-page">
      <div className="at-noise" aria-hidden="true" />

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border/70 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center gap-8 px-5 py-4">
          <a href="#top" className="group flex items-center">
            <div className="h-14 w-full max-w-[680px] sm:h-16 lg:h-[72px]">
              <img
                src="/assets/alianza-trinchero-logo.svg"
                alt="Alianza Trinchero SRL"
                className="h-full w-full object-contain object-left"
              />
            </div>
          </a>

          <nav className="hidden flex-1 items-center justify-center gap-10 md:flex">
            {nav.map((n) => (
              <NavLink key={n.id} href={`#${n.id}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 md:flex">

            <Button
              asChild
              variant="cta"
              className="rounded-full px-6 py-2 transition-transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <a href={waDefaultUrl} target="_blank" rel="noreferrer">
                Solicitar cotización
              </a>
            </Button>
          </div>

          <div className="md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full border-border/70 bg-white/40"
                >
                  Menú
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="at-glass">
                <SheetHeader>
                  <SheetTitle className="font-semibold">Navegación</SheetTitle>
                </SheetHeader>

                <div className="mt-6 flex flex-col gap-4">
                  {nav.map((n) => (
                    <NavLink
                      key={n.id}
                      href={`#${n.id}`}
                      onClick={() => setMobileOpen(false)}
                    >
                      {n.label}
                    </NavLink>
                  ))}

                  <Separator className="my-2" />

                  <Button
                    asChild
                    className="w-full rounded-full transition-transform hover:scale-[1.01] active:scale-[0.99]"
                  >
                    <a href={waDefaultUrl} target="_blank" rel="noreferrer">
                      Solicitar cotización
                    </a>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Hero */}
      <main id="top">
        <section className="relative overflow-hidden">
          <div className="at-hero-grid" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-white/40 via-white/20 to-white/10" aria-hidden="true" />
          <div className="relative z-10 mx-auto max-w-6xl px-5 pb-10 pt-14 md:pb-16 md:pt-20">
            <div className="grid gap-10 md:grid-cols-[1.15fr_.85fr] md:items-end">
              <div>

                <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground md:text-6xl">
                  {mock.company.tagline}
                </h1>
                <p className="at-prose at-justify mt-5 text-lg leading-relaxed text-muted-foreground md:text-xl">
                  {mock.company.subtagline}
                </p>

                <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button
                    asChild
                    size="lg"
                    variant="cta"
                    className="rounded-full px-6 transition-transform hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <a href={waDefaultUrl} target="_blank" rel="noreferrer">
                      Solicitar cotización
                      <ArrowRight className="ml-2" />
                    </a>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="rounded-full border-border/70 bg-white/40 px-6 hover:bg-secondary"
                  >
                    <a href="#servicios">Ver servicios</a>
                  </Button>
                </div>

                <div className="mt-10 grid gap-3 sm:grid-cols-3">
                  <Card className="at-glass">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Factory className="h-5 w-5 text-primary" />
                        <div>
                          <div className="text-sm font-semibold">Planta propia</div>
                          <div className="text-xs text-muted-foreground">
                            {mock.company.locationShort}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="at-glass">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Gauge className="h-5 w-5 text-primary" />
                        <div>
                          <div className="text-sm font-semibold">Capacidad real</div>
                          <div className="text-xs text-muted-foreground">
                            {mock.company.siteArea} + almacenaje
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="at-glass">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Truck className="h-5 w-5 text-primary" />
                        <div>
                          <div className="text-sm font-semibold">Logística</div>
                          <div className="text-xs text-muted-foreground">
                            {mock.company.coverage}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <Card className="at-glass">
                <CardHeader>
                  <CardTitle className="text-base">Contacto directo</CardTitle>
                  <CardDescription>
                    Respuesta rápida por WhatsApp o email.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3">
                    <a
                      className="flex items-center gap-3 rounded-xl border border-border/60 bg-card/40 p-3 transition-colors hover:bg-card/60"
                      href={`mailto:${mock.company.contact.email}`}
                    >
                      <Mail className="h-4 w-4 text-primary" />
                      <div>
                        <div className="text-sm font-semibold">Email</div>
                        <div className="text-xs text-muted-foreground">
                          {mock.company.contact.email}
                        </div>
                      </div>
                    </a>

                    <a
                      className="flex items-center gap-3 rounded-xl border border-border/60 bg-card/40 p-3 transition-colors hover:bg-card/60"
                      href={`tel:${mock.company.contact.phoneFixedDisplay.replace(/\s|\(|\)|-/g, "")}`}
                    >
                      <Phone className="h-4 w-4 text-primary" />
                      <div>
                        <div className="text-sm font-semibold">Teléfono</div>
                        <div className="text-xs text-muted-foreground">
                          {mock.company.contact.phoneFixedDisplay}
                        </div>
                      </div>
                    </a>

                    <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-card/30 p-3">
                      <MapPin className="h-4 w-4 text-primary" />
                      <div>
                        <div className="text-sm font-semibold">Dirección</div>
                        <div className="text-xs text-muted-foreground">
                          {mock.company.contact.address}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <Button
                    asChild
                    className="w-full rounded-full transition-transform hover:scale-[1.01] active:scale-[0.99]"
                  >
                    <a href={waDefaultUrl} target="_blank" rel="noreferrer">
                      Abrir WhatsApp
                      <ArrowRight className="ml-2" />
                    </a>
                  </Button>

                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Si estás definiendo especificación (granulometría, volumen,
                    aplicación), te ayudamos a armarla para cotizar.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Quiénes somos */}
        <section id="quienes" className="at-section mx-auto max-w-6xl px-5 py-14 lg:py-16">
          <div className="grid gap-10">
            <div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-primary md:text-4xl">
                Quiénes somos
              </h2>
              <div className="at-justify mt-4 space-y-4 text-lg leading-relaxed text-muted-foreground md:text-xl max-w-none">
                <p>
                  Alianza Trinchero es una empresa familiar fundada en 2025 a partir de la fusión de Carlos Alberto Trinchero y Molinos Alianza SRL, dos firmas con más de 50 años de trayectoria en el mercado argentino de minerales industriales. Nuestra historia comienza en 1973, cuando Carlos Trinchero dio el puntapié inicial a un proyecto basado en el trabajo, el compromiso y la visión de abastecer con calidad y constancia al sector industrial argentino.
                </p>
                <p>
                  A lo largo de las distintas etapas económicas y productivas del país, hemos sostenido y fortalecido esos valores fundacionales, consolidando una trayectoria marcada por la responsabilidad, la inversión continua y la cercanía con nuestros clientes. Hoy, esa experiencia se proyecta en una nueva etapa que integra tradición e innovación bajo una misma identidad.
                </p>
                <p>
                  Desde nuestro predio de 5.000 m² ubicado en el Parque Industrial de Lanús Este, operamos plantas de molienda y almacenamiento que nos permiten brindar respuestas ágiles y eficientes a clientes del Gran Buenos Aires y de todo el país. Nos especializamos en la molienda y comercialización de minerales no metalíferos, abasteciendo a industrias como vidrio, cerámica, siderurgia, esmaltes, plásticos, caucho, nutrición animal, pinturas, revestimientos y construcción, entre otras. Asimismo, ofrecemos servicios de molienda y fasón para terceros, adaptándonos a los requerimientos técnicos y productivos de cada proyecto.
                </p>
                <p>
                  En Alianza Trinchero combinamos experiencia, infraestructura y compromiso para seguir acompañando el desarrollo de la industria nacional, con la misma convicción que dio origen a nuestra historia.
                </p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                "Planta industrial propia",
                "Molinos a martillos, pendulares, a bolas, trituradoras y zarandas",
                "Producción, comercialización y servicios a terceros",
                "Enfoque en continuidad de abastecimiento, cumplimiento y calidad",
                "Cobertura nacional",
              ].map((t) => (
                <div
                  key={t}
                  className="group flex items-start gap-3 rounded-2xl border border-border/60 bg-white p-4 shadow-sm transition-colors hover:bg-secondary"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
                  <div className="text-sm font-medium text-foreground/90">{t}</div>
                </div>
              ))}
            </div>
          </div>

        </section>



        {/* Servicios */}
        <section id="servicios" className="at-section relative overflow-hidden py-14 lg:py-16">
          <div className="at-services-bg" aria-hidden="true" />
          <div className="relative z-10 mx-auto max-w-6xl px-5">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div>
                <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
                  Servicios
                </h2>
                <p className="at-prose at-justify mt-3 text-lg text-muted-foreground md:text-xl">
                  Provisión de minerales no metalíferos y servicios a terceros según
                  especificación.
                </p>
              </div>
              <div />
            </div>

          <div className="mt-8 grid gap-8 md:grid-cols-2 md:items-start">
            <Card className="border-border/60 bg-white">
              <CardHeader>
                <CardTitle className="text-base text-primary">
                  Producción y comercialización
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Molienda y variedad de minerales no metalíferos en múltiples
                  granulometrías y stock permanente.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {["Molienda", "Variedad de minerales no metalíferos", "Múltiples granulometrías", "Stock permanente"].map(
                  (t) => (
                    <div
                      key={t}
                      className="flex items-start gap-3 rounded-xl border border-border/60 bg-card/60 p-4"
                    >
                      <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" />
                      <div className="text-sm text-foreground/90">{t}</div>
                    </div>
                  ),
                )}
              </CardContent>
            </Card>

            <Card className="border-border/60 bg-white">
              <CardHeader>
                <CardTitle className="text-base text-primary">
                  Fasón a terceros
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Servicios de molienda, fraccionamiento y envasado a terceros
                  según especificaciones.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {["Servicios de molienda", "Fraccionamiento", "Envasado", "Según especificaciones"].map(
                  (t) => (
                    <div
                      key={t}
                      className="flex items-start gap-3 rounded-xl border border-border/60 bg-card/60 p-4"
                    >
                      <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" />
                      <div className="text-sm text-foreground/90">{t}</div>
                    </div>
                  ),
                )}
              </CardContent>
            </Card>
          </div>

          <div className="mt-8">
            <Card className="at-glass">
              <CardHeader>
                <CardTitle className="text-base">Preguntas frecuentes</CardTitle>
                <CardDescription>
                  Respuestas cortas para avanzar con la consulta.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {mock.faqs.map((f, idx) => (
                    <AccordionItem key={f.q} value={`faq-${idx}`}>
                      <AccordionTrigger>{f.q}</AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
          </div>


        </section>

        {/* Productos (Minerales + Industrias) */}
        <section id="productos" className="at-section bg-card/20">
          <div className="mx-auto max-w-6xl px-5 py-14 lg:py-16">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div>
                <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
                  Productos
                </h2>
                <p className="at-prose at-justify mt-3 text-lg text-muted-foreground md:text-xl">
                  Cargas minerales y aplicaciones industriales.
                </p>
              </div>
              <div />
            </div>

            <div className="mt-8 grid gap-10">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Minerales
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {mock.minerals.map((m) => (
                    <Card
                      key={m.name}
                      className="group border-border/60 bg-card/30 transition-colors hover:bg-card/40"
                    >
                      <CardHeader>
                        <CardTitle className="text-base text-primary">
                          {m.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="at-clamp-3 text-sm text-muted-foreground">
                          {m.uses}
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {(m.grades || []).length > 0 ? (
                            m.grades.map((g) => (
                              <Badge
                                key={`${m.name}-${g}`}
                                className="rounded-full bg-white text-primary border border-border"
                              >
                                {g}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              Granulometría según especificación
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="hidden" />
            </div>
          </div>
        </section>

        {/* Contacto */}
        <section id="contacto" className="at-section relative overflow-hidden py-14 lg:py-16">
          <div className="at-contacto-bg" aria-hidden="true" />
          <div className="relative z-10 mx-auto max-w-6xl px-5">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div>
                <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
                  Contacto
                </h2>
              </div>
              <div />
            </div>

            <div className="mt-8 grid gap-6 md:grid-cols-2">
            {/* Columna izquierda: Datos + Mapa */}
            <div className="flex flex-col gap-4">
              <Card className="border-border/60 bg-card/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Datos</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="rounded-xl border border-border/60 bg-card/30 p-3">
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-primary" />
                      <div>
                        <div className="text-sm font-semibold">Email</div>
                        <a
                          className="text-sm text-muted-foreground hover:text-foreground"
                          href={`mailto:${mock.company.contact.email}`}
                        >
                          {mock.company.contact.email}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-border/60 bg-card/30 p-3">
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-primary" />
                      <div className="flex flex-wrap gap-x-6 gap-y-1">
                        <div>
                          <span className="text-sm font-semibold">Fijo: </span>
                          <a
                            className="text-sm text-muted-foreground hover:text-foreground"
                            href={`tel:${mock.company.contact.phoneFixedDisplay.replace(/\s|\(|\)|-/g, "")}`}
                          >
                            {mock.company.contact.phoneFixedDisplay}
                          </a>
                        </div>
                        <div>
                          <span className="text-sm font-semibold">Cel: </span>
                          <a
                            className="text-sm text-muted-foreground hover:text-foreground"
                            href={`tel:${mock.company.contact.phoneMobileDisplay.replace(/\s|\(|\)|-/g, "")}`}
                          >
                            {mock.company.contact.phoneMobileDisplay}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-border/60 bg-card/30 p-3">
                    <div className="flex items-center gap-3">
                      <MapPin className="h-4 w-4 text-primary" />
                      <div>
                        <div className="text-sm font-semibold">Dirección</div>
                        <div className="text-sm text-muted-foreground">
                          {mock.company.contact.address}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Mapa de Google Maps */}
              <div className="flex-1 min-h-[200px] rounded-xl overflow-hidden border border-border/60 shadow-sm">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3281.7!2d-58.3987!3d-34.7097!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x95a3331d5f8b5555%3A0x1234567890abcdef!2sGervasio%20A.%20de%20Posadas%20775%2C%20Lan%C3%BAs%20Oeste%2C%20Provincia%20de%20Buenos%20Aires!5e0!3m2!1ses!2sar!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: "200px" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación Alianza Trinchero"
                />
              </div>
            </div>

            {/* Columna derecha: Formulario */}
            <Card className="at-glass">
              <CardHeader>
                <CardTitle className="text-base">Escribinos</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={onSubmit} className="grid gap-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <div className="mb-1 text-xs text-muted-foreground">
                        Nombre
                      </div>
                      <Input
                        value={form.nombre}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, nombre: e.target.value }))
                        }
                        placeholder="Tu nombre"
                        className="bg-background/40"
                        required
                      />
                    </div>
                    <div>
                      <div className="mb-1 text-xs text-muted-foreground">
                        Empresa
                      </div>
                      <Input
                        value={form.empresa}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, empresa: e.target.value }))
                        }
                        placeholder="Empresa / Planta"
                        className="bg-background/40"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <div className="mb-1 text-xs text-muted-foreground">
                        Email
                      </div>
                      <Input
                        value={form.email}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, email: e.target.value }))
                        }
                        placeholder="email@empresa.com"
                        type="email"
                        className="bg-background/40"
                        required
                      />
                    </div>
                    <div>
                      <div className="mb-1 text-xs text-muted-foreground">
                        Teléfono
                      </div>
                      <Input
                        value={form.telefono}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            telefono: formatArMobilePhone(e.target.value),
                          }))
                        }
                        placeholder="11 1234 1234"
                        inputMode="numeric"
                        autoComplete="tel"
                        className="bg-background/40"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <div className="mb-1 text-xs text-muted-foreground">
                      Mensaje
                    </div>
                    <Textarea
                      value={form.mensaje}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, mensaje: e.target.value }))
                      }
                      placeholder="Mineral, granulometría objetivo o aplicación, volumen y destino..."
                      className="min-h-[120px] bg-background/40"
                      required
                    />
                  </div>

                  <div className="grid gap-3">
                    <Button
                      type="submit"
                      variant="cta"
                      className="rounded-full transition-transform hover:scale-[1.01] active:scale-[0.99]"
                    >
                      Enviar y abrir WhatsApp
                      <ArrowRight className="ml-2" />
                    </Button>
                  </div>

                  <div className="text-xs text-muted-foreground">
                    Al enviar guardamos la consulta y abrimos WhatsApp con el
                    mensaje listo.
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-border/60 bg-white">
          <div className="mx-auto grid max-w-6xl gap-6 px-5 py-10 md:grid-cols-2 md:items-center">
            <div>
              <div className="flex items-center gap-3">
                <div className="h-12 w-full max-w-[420px] sm:h-14">
                  <img
                    src="/assets/alianza-trinchero-logo.svg"
                    alt="Alianza Trinchero SRL"
                    className="h-full w-full object-contain object-left"
                  />
                </div>
              </div>
              <div className="mt-4 text-xs text-muted-foreground">
                {mock.company.tagline}
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                {mock.company.contact.address}
              </div>
            </div>

            <div className="flex flex-col gap-3 md:items-end">
              <div className="flex flex-wrap gap-3">
                <a
                  className="text-sm text-muted-foreground hover:text-foreground"
                  href={`mailto:${mock.company.contact.email}`}
                >
                  {mock.company.contact.email}
                </a>
                <span className="text-sm text-muted-foreground">•</span>
                <a
                  className="text-sm text-muted-foreground hover:text-foreground"
                  href={waDefaultUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {mock.company.contact.phoneMobileDisplay} (WhatsApp)
                </a>
              </div>
              <div className="text-xs text-muted-foreground">
                © {new Date().getFullYear()} {mock.company.name}. Todos los derechos
                reservados.
              </div>
            </div>
          </div>
        </footer>
      </main>

      <WhatsAppFab
        phoneDigits={mock.whatsapp.phoneDigits}
        message={mock.whatsapp.defaultMessage}
      />
    </div>
  );
}
